// Sovereign Signal - client-side E2E cryptography (Web Crypto API).
// The server NEVER sees plaintext or unwrapped keys. All operations run here.

const enc = new TextEncoder();
const dec = new TextDecoder();

// ---- base64 helpers ----
export const buf2b64 = (buf) => {
  const bytes = new Uint8Array(buf);
  let bin = "";
  for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
  return btoa(bin);
};
export const b642buf = (b64) => {
  const bin = atob(b64);
  const bytes = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
  return bytes.buffer;
};

const randomIv = () => crypto.getRandomValues(new Uint8Array(12));

// ---- Identity keypair (ECDH P-256) ----
export async function generateIdentityKeyPair() {
  const kp = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, true, ["deriveKey"]);
  const privateKeyJwk = await crypto.subtle.exportKey("jwk", kp.privateKey);
  const publicKeyRaw = await crypto.subtle.exportKey("raw", kp.publicKey);
  return { privateKeyJwk, publicKeyB64: buf2b64(publicKeyRaw) };
}

export const importPrivateKey = (jwk) =>
  crypto.subtle.importKey("jwk", jwk, { name: "ECDH", namedCurve: "P-256" }, false, ["deriveKey"]);

export const importPublicKeyB64 = (b64) =>
  crypto.subtle.importKey("raw", b642buf(b64), { name: "ECDH", namedCurve: "P-256" }, false, []);

// ---- PBKDF2 wrap key from passphrase ----
export async function deriveWrapKey(passphrase, saltB64) {
  const baseKey = await crypto.subtle.importKey("raw", enc.encode(passphrase), "PBKDF2", false, ["deriveKey"]);
  return crypto.subtle.deriveKey(
    { name: "PBKDF2", salt: b642buf(saltB64), iterations: 200000, hash: "SHA-256" },
    baseKey,
    { name: "AES-GCM", length: 256 },
    false,
    ["encrypt", "decrypt"]
  );
}

export const newSaltB64 = () => buf2b64(crypto.getRandomValues(new Uint8Array(16)));

// A separate, non-decrypting verifier derived from the recovery phrase. The server
// stores bcrypt(recovery_auth) so it can authorize a password reset by proving the
// user holds the phrase - without ever learning the phrase or being able to decrypt.
export async function deriveRecoveryAuth(phrase, salt2B64) {
  const baseKey = await crypto.subtle.importKey("raw", enc.encode(phrase + "|auth"), "PBKDF2", false, ["deriveBits"]);
  const bits = await crypto.subtle.deriveBits(
    { name: "PBKDF2", salt: b642buf(salt2B64), iterations: 200000, hash: "SHA-256" },
    baseKey, 256
  );
  return buf2b64(bits);
}

// Wrap/unwrap the identity private key (stored as JWK) with a passphrase-derived key
export async function wrapPrivateJwk(privateKeyJwk, wrapKey) {
  const iv = randomIv();
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, wrapKey, enc.encode(JSON.stringify(privateKeyJwk)));
  return { ctB64: buf2b64(ct), ivB64: buf2b64(iv) };
}
export async function unwrapPrivateJwk(ctB64, ivB64, wrapKey) {
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: b642buf(ivB64) }, wrapKey, b642buf(ctB64));
  return JSON.parse(dec.decode(pt));
}

// ---- Network key (AES-GCM 256) shared by approved members ----
export async function generateNetworkKey() {
  const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, true, ["encrypt", "decrypt"]);
  return buf2b64(await crypto.subtle.exportKey("raw", key));
}
export const importAesKeyB64 = (b64) =>
  crypto.subtle.importKey("raw", b642buf(b64), { name: "AES-GCM" }, false, ["encrypt", "decrypt"]);

// ---- Envelope: wrap the network key to a member's public key via ephemeral ECDH ----
export async function wrapKeyToPublic(networkKeyRawB64, recipientPubB64) {
  const eph = await crypto.subtle.generateKey({ name: "ECDH", namedCurve: "P-256" }, true, ["deriveKey"]);
  const recipientPub = await importPublicKeyB64(recipientPubB64);
  const aesKey = await crypto.subtle.deriveKey(
    { name: "ECDH", public: recipientPub }, eph.privateKey,
    { name: "AES-GCM", length: 256 }, false, ["encrypt"]
  );
  const iv = randomIv();
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, aesKey, b642buf(networkKeyRawB64));
  const ephPubRaw = await crypto.subtle.exportKey("raw", eph.publicKey);
  return { wrappedB64: buf2b64(ct), ivB64: buf2b64(iv), ephemeralPubB64: buf2b64(ephPubRaw) };
}
export async function unwrapKeyFromEnvelope(wrappedB64, ivB64, ephemeralPubB64, myPrivateKey) {
  const ephPub = await importPublicKeyB64(ephemeralPubB64);
  const aesKey = await crypto.subtle.deriveKey(
    { name: "ECDH", public: ephPub }, myPrivateKey,
    { name: "AES-GCM", length: 256 }, false, ["decrypt"]
  );
  const raw = await crypto.subtle.decrypt({ name: "AES-GCM", iv: b642buf(ivB64) }, aesKey, b642buf(wrappedB64));
  return buf2b64(raw);
}

// ---- DM shared key via ECDH(myPriv, theirPub) ----
export async function deriveDmKey(myPrivateKey, theirPubB64) {
  const theirPub = await importPublicKeyB64(theirPubB64);
  return crypto.subtle.deriveKey(
    { name: "ECDH", public: theirPub }, myPrivateKey,
    { name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]
  );
}

// ---- Symmetric encrypt/decrypt with an AES-GCM CryptoKey ----
export async function encryptText(key, plaintext) {
  const iv = randomIv();
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, enc.encode(plaintext));
  return { ctB64: buf2b64(ct), ivB64: buf2b64(iv) };
}
export async function decryptText(key, ctB64, ivB64) {
  const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv: b642buf(ivB64) }, key, b642buf(ctB64));
  return dec.decode(pt);
}
export async function encryptBytes(key, arrayBuffer) {
  const iv = randomIv();
  const ct = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, arrayBuffer);
  return { ctBuf: ct, ivB64: buf2b64(iv) };
}
export async function decryptBytes(key, arrayBuffer, ivB64) {
  return crypto.subtle.decrypt({ name: "AES-GCM", iv: b642buf(ivB64) }, key, arrayBuffer);
}

// ---- Chunked media (large videos) ----
// Big videos can't be encrypted/decrypted in one pass without blowing up browser
// memory on phones. Instead we frame the ciphertext into self-describing chunks:
//   [8-byte MAGIC] then repeated frames: [4-byte BE ct length][12-byte IV][ciphertext]
// Each chunk is a separate AES-GCM operation with its own IV. The MAGIC header lets
// the decryptor auto-detect chunked media, so no schema/metadata change is needed
// and legacy single-blob media keeps working unchanged.
export const MEDIA_CHUNK_MAGIC = new Uint8Array([0x53, 0x56, 0x53, 0x4d, 0x45, 0x44, 0x30, 0x31]); // "SVSMED01"

export const isChunkedMedia = (arrayBuffer) => {
  if (!arrayBuffer || arrayBuffer.byteLength < MEDIA_CHUNK_MAGIC.length) return false;
  const head = new Uint8Array(arrayBuffer, 0, MEDIA_CHUNK_MAGIC.length);
  for (let i = 0; i < MEDIA_CHUNK_MAGIC.length; i++) if (head[i] !== MEDIA_CHUNK_MAGIC[i]) return false;
  return true;
};

// Decrypt a framed chunked-media buffer into a Blob of the given mime, one chunk at
// a time (bounded peak memory ≈ one chunk of plaintext).
export async function decryptChunkedToBlob(key, arrayBuffer, mime) {
  const bytes = new Uint8Array(arrayBuffer);
  const dv = new DataView(arrayBuffer);
  let off = MEDIA_CHUNK_MAGIC.length;
  const parts = [];
  while (off + 16 <= bytes.length) {
    const ctLen = dv.getUint32(off); off += 4;
    const iv = bytes.slice(off, off + 12); off += 12;
    const ct = arrayBuffer.slice(off, off + ctLen); off += ctLen;
    const pt = await crypto.subtle.decrypt({ name: "AES-GCM", iv }, key, ct);
    parts.push(pt);
  }
  return new Blob(parts, { type: mime });
}

// ---- Recovery phrase (12 words, ~84 bits from a 128-word list) ----
const WORDS = "anchor,arbor,arrow,atlas,aura,axiom,beacon,bedrock,bishop,bloom,bolt,brave,bridge,bright,bronze,canyon,cedar,chart,cipher,cobalt,comet,compass,coral,crest,crown,crystal,delta,dawn,ember,envoy,epoch,falcon,forge,fortune,gable,galaxy,garnet,gateway,glacier,granite,harbor,haven,helix,herald,honor,horizon,ingot,ivory,jade,keystone,kindred,lantern,ledger,legacy,liberty,lighthouse,lumen,marble,meridian,monarch,mosaic,nexus,noble,north,oak,oracle,orbit,outpost,palace,pillar,pioneer,polar,prism,quartz,quill,radiant,realm,refuge,relay,rampart,saber,sage,sentinel,signal,silver,solace,sovereign,sphinx,spire,summit,talisman,tarn,temple,thunder,timber,titan,token,tower,trade,treaty,tribute,trove,unity,valor,vanguard,vault,vector,verdant,vertex,vessel,vigil,vista,voyage,warden,watch,willow,zenith,zephyr,acre,amber,banner,citadel,domain,echo,fable,grove,island,journey,kingdom".split(",");

export function generateRecoveryPhrase() {
  const rnd = crypto.getRandomValues(new Uint32Array(12));
  return Array.from(rnd).map((n) => WORDS[n % WORDS.length]).join(" ");
}
export const normalizePhrase = (p) => p.trim().toLowerCase().split(/\s+/).join(" ");


// ---- Shamir Secret Sharing over GF(256), powers 2-of-N break-glass ----
const GF_EXP = new Uint8Array(512);
const GF_LOG = new Uint8Array(256);
(function initGF() {
  let x = 1;
  for (let i = 0; i < 255; i++) { GF_EXP[i] = x; GF_LOG[x] = i; x ^= (x << 1); if (x & 0x100) x ^= 0x11b; }
  for (let i = 255; i < 512; i++) GF_EXP[i] = GF_EXP[i - 255];
})();
const gfMul = (a, b) => (a === 0 || b === 0) ? 0 : GF_EXP[GF_LOG[a] + GF_LOG[b]];
const gfDiv = (a, b) => a === 0 ? 0 : GF_EXP[(GF_LOG[a] - GF_LOG[b] + 255) % 255];

// Split a secret (Uint8Array) into n shares, any k of which reconstruct it.
// Each returned share is a Uint8Array: [x-coord, ...y bytes].
export function shamirSplit(secret, n, k = 2) {
  const shares = [];
  for (let xi = 1; xi <= n; xi++) { const s = new Uint8Array(secret.length + 1); s[0] = xi; shares.push(s); }
  for (let bi = 0; bi < secret.length; bi++) {
    const coeffs = new Uint8Array(k); coeffs[0] = secret[bi];
    const rnd = crypto.getRandomValues(new Uint8Array(Math.max(0, k - 1)));
    for (let j = 1; j < k; j++) coeffs[j] = rnd[j - 1];
    for (let si = 0; si < n; si++) {
      const x = shares[si][0]; let y = 0, xp = 1;
      for (let j = 0; j < k; j++) { y ^= gfMul(coeffs[j], xp); xp = gfMul(xp, x); }
      shares[si][bi + 1] = y;
    }
  }
  return shares;
}

// Combine >= k shares (each [x, ...y]) back into the secret Uint8Array (Lagrange at x=0).
export function shamirCombine(shares) {
  const len = shares[0].length - 1;
  const secret = new Uint8Array(len);
  const xs = shares.map((s) => s[0]);
  for (let bi = 0; bi < len; bi++) {
    let acc = 0;
    for (let i = 0; i < shares.length; i++) {
      let num = 1, den = 1;
      for (let j = 0; j < shares.length; j++) { if (j === i) continue; num = gfMul(num, xs[j]); den = gfMul(den, xs[i] ^ xs[j]); }
      acc ^= gfMul(shares[i][bi + 1], gfDiv(num, den));
    }
    secret[bi] = acc;
  }
  return secret;
}
